import { useCallback, useEffect, useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import {
  trackEvent,
  setPendingCheckout,
  readPendingCheckout,
  clearPendingCheckout,
} from "@/lib/analytics";

export const TGC_CART_URL = "https://www.thegamecrafter.com/cart";
// If the automatic hand-off hasn't taken the tab away by now, something blocked
// it — surface a manual link so the shopper can still reach their cart.
const HANDOFF_TIMEOUT_MS = 6000;

interface StartCheckoutOptions {
  email: string;
  newsletterOptIn: boolean;
}

/**
 * Owns the Game Crafter cart hand-off: creates the remote cart, records the
 * shopper's email, and navigates the tab to the cart adoption URL.
 */
export const useGameCrafterCheckout = () => {
  const { items, subtotal, clearCart } = useCart();
  const [sending, setSending] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);

  const failWith = useCallback((message: string, url: string | null) => {
    trackEvent("checkout_handoff_failed", { reason: message, has_fallback: Boolean(url) });
    clearPendingCheckout();
    setCheckoutError(message);
    setFallbackUrl(url);
    setSending(false);
  }, []);

  // When the shopper comes back to the site (Back button or a new visit in the
  // same tab session), ask TheGameCrafter whether that cart was completed.
  useEffect(() => {
    const pending = readPendingCheckout();
    if (!pending) return;
    clearPendingCheckout();

    const elapsed_ms = Date.now() - pending.startedAt;
    trackEvent("checkout_returned", {
      elapsed_ms,
      value: pending.value,
      item_count: pending.itemCount,
    });

    if (!pending.cartId) return;
    supabase.functions
      .invoke("gamecrafter-cart", { body: { action: "status", cartId: pending.cartId } })
      .then(({ data }) => {
        const completed = (data as { completed?: boolean | null } | null)?.completed;
        if (completed === true) {
          trackEvent("checkout_completed", {
            value: pending.value,
            item_count: pending.itemCount,
            elapsed_ms,
            currency: "USD",
          });
          clearCart();
        } else if (completed === false) {
          trackEvent("checkout_abandoned", { value: pending.value, item_count: pending.itemCount, elapsed_ms });
        }
      })
      .catch(() => {
        /* status is best-effort only */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = useCallback(
    async ({ email, newsletterOptIn }: StartCheckoutOptions) => {
      if (items.length === 0 || sending) return;

      setSending(true);
      setCheckoutError(null);
      setFallbackUrl(null);

      const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

      trackEvent("checkout_takeover_started", {
        value: subtotal,
        item_count: itemCount,
        items: items.map((i) => ({ id: i.gameId, name: i.title, quantity: i.quantity })),
      });

      let adoptUrl: string | undefined;

      try {
        // The cart is built server-side with TheGameCrafter API, then this tab
        // adopts it by visiting /cart/<id>.
        const { data, error } = await supabase.functions.invoke("gamecrafter-cart", {
          body: {
            items: items.map((item) => ({ gameId: item.gameId, quantity: item.quantity })),
          },
        });

        adoptUrl = (data as { adoptUrl?: string } | null)?.adoptUrl;
        const cartId = (data as { cartId?: string } | null)?.cartId;

        if (error || !adoptUrl) {
          throw new Error(
            (data as { error?: string } | null)?.error ??
              "We couldn't prepare your Game Crafter order. Please try again.",
          );
        }

        // Record the lead + optional newsletter signup. Never block the hand-off.
        await Promise.allSettled([
          supabase.from("checkout_leads").insert({
            email,
            items: items.map((i) => ({ id: i.gameId, sku: i.sku, title: i.title, quantity: i.quantity })),
            subtotal,
            item_count: itemCount,
            newsletter_opt_in: newsletterOptIn,
            cart_id: cartId ?? null,
          }),
          newsletterOptIn
            ? supabase.functions.invoke("newsletter-subscribe", { body: { email } })
            : Promise.resolve(),
        ]);

        // Watchdog: if the browser hasn't left this page by now (blocked
        // navigation, offline, slow 302), give the shopper a link they can click.
        const cartUrl = adoptUrl;
        const watchdog = window.setTimeout(() => {
          failWith(
            "Taking longer than expected to reach TheGameCrafter. Use the link below to finish your order.",
            cartUrl,
          );
        }, HANDOFF_TIMEOUT_MS);
        window.addEventListener("pagehide", () => window.clearTimeout(watchdog), { once: true });

        setPendingCheckout({ cartId, startedAt: Date.now(), value: subtotal, itemCount });

        trackEvent("checkout_redirected", { cart_id: cartId, value: subtotal, item_count: itemCount });

        // Single navigation: /cart/<id> is a 302 that sets the Game Crafter cart
        // cookie and lands on their cart page. `assign` keeps a history entry so
        // Back returns to our site.
        window.location.assign(adoptUrl);
      } catch (error) {
        failWith(
          error instanceof Error ? error.message : "We couldn't open your Game Crafter cart. Please try again.",
          adoptUrl ?? null,
        );
      }
    },
    [items, sending, subtotal, failWith],
  );

  return { sending, checkoutError, fallbackUrl, startCheckout };
};
